import type {StateCreator} from 'zustand';
import type {AssignmentSettingsFormSchemaType} from 'client/components/ContentDiscovery/AssignmentSettings/AssignmentSettingsForm/AssignmentSettingsForm.schema';
import type {LockAuthIntents} from 'client/components/LockAuthorization/lockAuthWrites';
import type {ContentDiscoveryStoreState} from 'client/components/ContentDiscovery/store/ContentDiscoveryStore.types';

type SettingsFormState = {
  formData: AssignmentSettingsFormSchemaType | null;
  settingsValidationError: boolean;
  hasDueDate: boolean;
  /**
   * What the admin chose per scope with the restricted-content control this session. Lives here
   * because the controls are rendered by the settings form — remounted per scope route — while the
   * writes are issued by the builder layout, and a write the admin did not ask for is the difference
   * between granting a class and revoking it.
   */
  lockAuthIntents: LockAuthIntents;
  /**
   * The assignment those intents belong to. Recording against a different assignment resets them,
   * which is what lets the intents survive without a mount-time clear: the settings form remounts
   * on every scope route and can be unmounted by the parent's loading gate mid-session, so clearing
   * on mount discards intent the admin has already expressed.
   */
  lockAuthIntentsAssignmentId: string | null;
};

type SettingsFormActions = {
  setFormData: (formData: AssignmentSettingsFormSchemaType) => void;
  setSettingsValidationError: (error: boolean) => void;
  setHasDueDate: (hasDueDate: boolean) => void;
  recordLockAuthIntent: (intent: {
    assignmentId?: string | null;
    classroomId?: string;
    value: boolean;
  }) => void;
  adoptLockAuthIntents: (assignmentId: string) => void;
  clearLockAuthIntents: () => void;
  reset: () => void;
};

const EMPTY_LOCK_AUTH_INTENTS: LockAuthIntents = {classrooms: {}};

const initialState: SettingsFormState = {
  formData: null,
  settingsValidationError: false,
  hasDueDate: false,
  lockAuthIntents: EMPTY_LOCK_AUTH_INTENTS,
  lockAuthIntentsAssignmentId: null
};

export type SettingsFormSlice = SettingsFormState & SettingsFormActions;

export const createSettingsFormSlice: StateCreator<
  ContentDiscoveryStoreState,
  [['zustand/immer', never]],
  [],
  SettingsFormSlice
> = (set, _): SettingsFormSlice => ({
  ...initialState,
  setFormData: async (formData) => {
    set((state) => {
      state.settingsForm.formData = formData as AssignmentSettingsFormSchemaType;
    });
  },
  setSettingsValidationError: async (error) => {
    set((state) => {
      state.settingsForm.settingsValidationError = error;
    });
  },
  setHasDueDate: async (hasDueDate) => {
    set((state) => {
      state.settingsForm.hasDueDate = hasDueDate;
    });
  },
  // Accumulates across the scope routes the form is remounted on. Publishes new state only when a
  // value actually changes: an unconditional assignment hands out a fresh object every render, and
  // the layout subscribes to this.
  recordLockAuthIntent: ({assignmentId = null, classroomId, value}) => {
    set((state) => {
      if (state.settingsForm.lockAuthIntentsAssignmentId !== assignmentId) {
        state.settingsForm.lockAuthIntentsAssignmentId = assignmentId;
        state.settingsForm.lockAuthIntents = EMPTY_LOCK_AUTH_INTENTS;
      }

      const current = state.settingsForm.lockAuthIntents;

      if (!classroomId || current.classrooms[classroomId] === value) {
        return;
      }

      state.settingsForm.lockAuthIntents = {
        classrooms: {...current.classrooms, [classroomId]: value}
      };
    });
  },
  // The create flow records intent before the assignment exists, so those records are keyed `null`.
  // A save mints the id and, for a save-as-draft, defers the lock-authorization writes — so the
  // records have to outlive it, and the key reset below fires on any mismatch. Adopting the new id
  // is what keeps them addressable once the next toggle starts supplying it.
  //
  // Only a `null` key is adopted. Records already keyed to a known assignment belong to that
  // assignment, and re-keying those is the cross-assignment bleed the key exists to stop.
  adoptLockAuthIntents: (assignmentId) => {
    set((state) => {
      if (state.settingsForm.lockAuthIntentsAssignmentId !== null) {
        return;
      }
      state.settingsForm.lockAuthIntentsAssignmentId = assignmentId;
    });
  },
  // Called once the save's lock-authorization writes have settled, and whenever the builder moves
  // to another assignment: intent is per-save, and a stale one turns the next save's untouched
  // classrooms into explicit revocations.
  clearLockAuthIntents: () => {
    set((state) => {
      if (Object.keys(state.settingsForm.lockAuthIntents.classrooms).length === 0) {
        return;
      }
      state.settingsForm.lockAuthIntents = EMPTY_LOCK_AUTH_INTENTS;
    });
  },
  reset: () => {
    set((state) => {
      state.settingsForm = {...state.settingsForm, ...initialState};
    });
  }
});
